import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Infrastructure.css";
import useReveal from "../hooks/useReveal";
import TokenRevealText from "../components/TokenRevealText";

const toolRoom = [
  "ELECTRA DRO Milling Machine (ELECTRONICA Make)",
  "ESTEAM DRO Milling Machine",
  "COSMOS CNC Machine",
  "SWASTIK Power Press Machine",
  "Johnson Shipman Surface Grinding Machines",
  "BFW Milling Machines",
  "Tool Craft EDM Machines",
  "DRO Milling Machines",
  "Electronic EDM Machines",
  "Turno Max Lathe Machines",
];

const toolRoomGallery = [
  {
    src: "/images/DSC_0145.jpg",
    alt: "Tool room equipment",
    fallback: "/images/C2P2.jpg",
  },
  {
    src: "/images/DSC_0145-2.jpg",
    alt: "Tool room machining setup",
    fallback: "/images/C2P.jpg",
  },
];

const printers3D = ["Formlabs 3D Printer"];

const moldingMachines = [
  {
    id: "180-ton",
    name: "Ferromatik Injection Molding",
    capacity: "180 Ton",
    type: "Horizontal",
    image: "/images/180-ton.jpg",
  },
  {
    id: "150-ton",
    name: "Ferromatik Injection Molding",
    capacity: "150 Ton",
    type: "Horizontal",
    image: "/images/150-ton.jpg",
  },
  {
    id: "110-ton",
    name: "Milacron Injection Molding",
    capacity: "110 Ton",
    type: "Horizontal",
    image: "/images/110-ton.jpg",
  },
  {
    id: "100-ton",
    name: "Vertical Injection Molding",
    capacity: "100 Ton",
    type: "Vertical",
    image: "/images/100-ton.jpg",
  },
  {
    id: "edm",
    name: "EDM Machine",
    capacity: "EDM",
    type: "Tool Room",
    image: "/images/edm.jpg",
  },
];

const facilityHighlights = [
  { label: "Built-up Area", value: "20,933 Sq. Ft." },
  { label: "High Precision Machining", value: "CNC + EDM" },
  { label: "Injection Molding", value: "100T–180T Machinery" },
  { label: "Quality Assurance", value: "In-house Lab" },
];

const Infrastructure = () => {
  useReveal();
  const [activeMachineId, setActiveMachineId] = useState(
    moldingMachines[0]?.id || "",
  );
  const [activeToolRoomImage, setActiveToolRoomImage] = useState(0);
  const [isAutoPaused, setIsAutoPaused] = useState(false);
  const activeMachine =
    moldingMachines.find((m) => m.id === activeMachineId) || moldingMachines[0];

  const goToPreviousToolRoomImage = () => {
    setActiveToolRoomImage(
      (currentIndex) =>
        (currentIndex - 1 + toolRoomGallery.length) % toolRoomGallery.length,
    );
  };

  const goToNextToolRoomImage = () => {
    setActiveToolRoomImage(
      (currentIndex) => (currentIndex + 1) % toolRoomGallery.length,
    );
  };

  useEffect(() => {
    if (toolRoomGallery.length < 2) return undefined;

    const intervalId = window.setInterval(() => {
      goToNextToolRoomImage();
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (isAutoPaused || moldingMachines.length < 2) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveMachineId((currentId) => {
        const currentIndex = moldingMachines.findIndex(
          (m) => m.id === currentId,
        );
        const nextIndex =
          currentIndex >= 0 ? (currentIndex + 1) % moldingMachines.length : 0;
        return moldingMachines[nextIndex].id;
      });
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [isAutoPaused]);
  return (
    <main className="infra-page page-enter">
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container reveal">
          <p className="section-eyebrow-light">
            <TokenRevealText text="Our Facility" speed="medium" />
          </p>
          <h1>
            <TokenRevealText
              text="Infrastructure & Manufacturing"
              speed="medium"
            />
          </h1>
          <p>
            <TokenRevealText
              text="A modern 20,933 sq. ft. facility equipped with advanced machinery, precision tooling, and quality systems."
              speed="medium"
            />
          </p>
        </div>
      </section>

      {/* ===== FACILITY OVERVIEW ===== */}
      <section id="facility" className="section infra-facility-section">
        <div className="container">
          <div className="grid-2">
            <div className="reveal reveal-left">
              <p className="section-eyebrow">
                <TokenRevealText text="Manufacturing Facility" speed="medium" />
              </p>
              <h2 className="section-title">
                <TokenRevealText
                  text="State-of-the-Art Facility"
                  speed="medium"
                />
              </h2>
              <div className="divider" />
              <p className="body-text">
                <TokenRevealText
                  text="Our facility is equipped with advanced manufacturing machines and a fully equipped tool room to ensure high precision and consistent quality across all production runs."
                  speed="medium"
                />
              </p>
              <ul className="infra-list">
                <li>
                  <TokenRevealText text="Built-up area of" speed="medium" />{" "}
                  <strong>
                    <TokenRevealText text="20,933 sq. ft." speed="medium" />
                  </strong>{" "}
                  <TokenRevealText
                    text="manufacturing facility"
                    speed="medium"
                  />
                </li>
                <li>
                  <TokenRevealText
                    text="Dedicated tool room and injection molding section"
                    speed="medium"
                  />
                </li>
                <li>
                  <TokenRevealText
                    text="High-precision machining equipment"
                    speed="medium"
                  />
                </li>
                <li>
                  <TokenRevealText
                    text="Quality inspection and testing facilities"
                    speed="medium"
                  />
                </li>
                <li>
                  <TokenRevealText
                    text="40+ skilled professionals on-site"
                    speed="medium"
                  />
                </li>
              </ul>
            </div>
            <div
              className="media-card reveal reveal-right"
              style={{ height: "380px" }}
            >
              <img
                src="/images/AR7A3732.JPG"
                alt="Factory Floor"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/images/C2P.jpg";
                }}
              />
            </div>
          </div>

          {/* Facility Highlights */}
          <div className="facility-highlights">
            {facilityHighlights.map((h, i) => (
              <div
                key={h.label}
                className={`fh-card reveal reveal-scale reveal-d${i + 1}`}
              >
                <div className="fh-value">
                  <TokenRevealText text={h.value} speed="medium" />
                </div>
                <div className="fh-label">
                  <TokenRevealText text={h.label} speed="medium" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TOOL ROOM ===== */}
      <section
        id="toolroom"
        className="section section-alt infra-toolroom-section"
      >
        <div className="container">
          <div className="grid-2">
            <div className="toolroom-carousel reveal reveal-left">
              <button
                type="button"
                className="toolroom-carousel-arrow toolroom-carousel-arrow-left"
                onClick={goToPreviousToolRoomImage}
                aria-label="Previous tool room image"
              >
                &#8249;
              </button>

              <div className="toolroom-carousel-viewport">
                <div
                  className="toolroom-carousel-track"
                  style={{
                    transform: `translateX(-${activeToolRoomImage * 100}%)`,
                  }}
                >
                  {toolRoomGallery.map((image) => (
                    <div className="toolroom-carousel-slide" key={image.src}>
                      <div className="media-card toolroom-carousel-frame">
                        <img
                          src={image.src}
                          alt={image.alt}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = image.fallback;
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="toolroom-carousel-arrow toolroom-carousel-arrow-right"
                onClick={goToNextToolRoomImage}
                aria-label="Next tool room image"
              >
                &#8250;
              </button>

              <div
                className="toolroom-carousel-dots"
                aria-label="Tool room image carousel"
              >
                {toolRoomGallery.map((image, index) => (
                  <button
                    key={image.src}
                    type="button"
                    className={`toolroom-carousel-dot ${index === activeToolRoomImage ? "active" : ""}`}
                    onClick={() => setActiveToolRoomImage(index)}
                    aria-label={`Show tool room image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            <div className="reveal reveal-right">
              <p className="section-eyebrow">
                <TokenRevealText text="Precision Tooling" speed="medium" />
              </p>
              <h2 className="section-title">
                <TokenRevealText text="Tool Room Equipment" speed="medium" />
              </h2>
              <div className="divider" />
              <p className="body-text">
                <TokenRevealText
                  text="Our tool room is equipped with a comprehensive range of precision machining equipment to manufacture high-quality molds and tooling components."
                  speed="medium"
                />
              </p>
              <ul className="toolroom-list">
                {toolRoom.map((item) => (
                  <li key={item}>
                    <span className="tr-bullet">&#10003;</span>
                    <span>
                      <TokenRevealText text={item} speed="medium" />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 3D PRINTING ===== */}
      <section id="3dprinting" className="section infra-printing-section">
        <div className="container">
          <div className="grid-2">
            <div className="reveal reveal-left">
              <p className="section-eyebrow">
                <TokenRevealText text="Rapid Prototyping" speed="medium" />
              </p>
              <h2 className="section-title">
                <TokenRevealText text="3D Printing Machine" speed="medium" />
              </h2>
              <div className="divider" />
              <p className="body-text">
                <TokenRevealText
                  text="Our advanced 3D printing capabilities enable rapid prototyping and concept validation, helping reduce development time and costs."
                  speed="medium"
                />
              </p>
              <ul className="toolroom-list">
                {printers3D.map((item) => (
                  <li key={item}>
                    <span className="tr-bullet">&#10003;</span>
                    <span>
                      <TokenRevealText text={item} speed="medium" />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="media-card reveal reveal-right"
              style={{ height: "380px" }}
            >
              <img
                src="/images/C2P2.jpg"
                alt="3D Printing Machine"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/images/C2P3.jpg";
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== INJECTION MOLDING MACHINES ===== */}
      <section id="machines" className="section infra-machines-section">
        <div className="container">
          <div
            className="reveal"
            style={{ textAlign: "center", marginBottom: "48px" }}
          >
            <p className="section-eyebrow">
              <TokenRevealText text="Production Capacity" speed="medium" />
            </p>
            <h2 className="section-title centered">
              <TokenRevealText
                text="Injection Molding Machines"
                speed="medium"
              />
            </h2>
            <div className="divider" style={{ margin: "12px auto 0" }} />
          </div>
          <div
            className="machines-grid"
            onMouseEnter={() => setIsAutoPaused(true)}
            onMouseLeave={() => setIsAutoPaused(false)}
          >
            {moldingMachines.map((m, i) => (
              <button
                key={m.id}
                type="button"
                className={`machine-card reveal reveal-scale reveal-d${(i % 3) + 1} ${activeMachineId === m.id ? "active" : ""}`}
                onClick={() => {
                  setActiveMachineId(m.id);
                  setIsAutoPaused(true);
                  window.setTimeout(() => setIsAutoPaused(false), 6000);
                }}
              >
                <div className="machine-capacity">
                  <TokenRevealText text={m.capacity} speed="medium" />
                </div>
                <div className="machine-name">
                  <TokenRevealText text={m.name} speed="medium" />
                </div>
                <div className="machine-type-badge">
                  <TokenRevealText text={m.type} speed="medium" />
                </div>
              </button>
            ))}
          </div>
          {activeMachine ? (
            <div className="machine-preview">
              <div className="machine-preview-title reveal">
                <TokenRevealText
                  text={`${activeMachine.capacity} Machine`}
                  speed="medium"
                />
              </div>
              <div className="machine-preview-frame media-card reveal">
                <img
                  src={activeMachine.image}
                  alt={`${activeMachine.capacity} Machine`}
                  className="machine-preview-img"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/images/C2P.jpg";
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="infra-cta">
        <div className="container infra-cta-inner">
          <div className="reveal reveal-left">
            <h2>
              <TokenRevealText
                text="Want to see our facility?"
                speed="medium"
              />
            </h2>
            <p>
              <TokenRevealText
                text="Contact us to schedule a facility visit or discuss your project requirements."
                speed="medium"
              />
            </p>
          </div>
          <Link to="/contact" className="btn-primary reveal reveal-right">
            Let's Connect
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Infrastructure;
